import CheckIcon from "@mui/icons-material/Check";
import { Box, ButtonBase, List, ListItem, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { SignupLogo } from "../../../auth/signup/SignupBrandPanel.js";
import {
  brandPanelBasis,
  fluid,
} from "../../../auth/signup/brandPanelSizing.js";
import { ONBOARDING_COLORS } from "./onboardingDesign.js";
import type {
  OnboardingStep,
  OnboardingStepId,
  OnboardingStepStatus,
} from "./onboardingFlowSteps.js";

interface Props {
  /** The overline above the steps, e.g. "Set-up steps". */
  heading: string;
  /** `null` renders the sidebar without a step list. */
  steps: OnboardingStep[] | null;
  /**
   * Makes the steps other than the current one and the goal links to their
   * stage: a completed one always, an upcoming one when the flow has more
   * than one step. Without it (e.g. behind the done card) every step is static.
   */
  onStepSelect?: (stepId: OnboardingStepId) => void;
}

/**
 * The navy sidebar: the logo (a link out of the wizard), an overline ("Set up your business") and the
 * wizard's steps with a number, a check once completed, and a filled marker
 * for the current one; the other steps are links to them. The last step is the
 * goal "Ready to go". On narrow screens it shrinks to a header band with
 * only the logo, since the "Step N of M" label next to the title already tells
 * the position.
 */
export const OnboardingStepSidebar = ({
  heading,
  steps,
  onStepSelect,
}: Props) => {
  const { t } = useTranslate(["Global", "OrganisationProfile"]);
  const titleId = React.useId();

  // Jumping ahead only means something with more than one step to jump between.
  const multiStep = (steps?.filter(step => step.id !== "done").length ?? 0) > 1;

  const stepLabels: Record<OnboardingStepId, string> = {
    business: t("Your business", { ns: "OrganisationProfile" }),
    property: t("Your property", { ns: "OrganisationProfile" }),
    trades: t("Your trades", { ns: "OrganisationProfile" }),
    done: t("Ready to go", { ns: "OrganisationProfile" }),
  };

  return (
    <Box
      component="aside"
      aria-labelledby={titleId}
      sx={{
        // The board's 400px at 1440px, growing on wider screens like the
        // signup brand panel (brandPanelSizing).
        flex: { md: `0 0 ${brandPanelBasis({ width: 400, share: 27.8 })}` },
        minWidth: { md: 280 },
        display: "flex",
        flexDirection: "column",
        // On a phone the same band as the signup header (SignupSplitLayout).
        px: { xs: 2, md: fluid(44) },
        pt: { xs: "calc(env(safe-area-inset-top) + 14px)", md: fluid(40) },
        pb: { xs: "14px", md: fluid(36) },
        bgcolor: ONBOARDING_COLORS.brandPanel,
        color: "common.white",
      }}
    >
      {/* A way out of the wizard: "/" drops the `ofs` search param (closing the
          wizard) and lands on the dashboard, like "Skip for now" at the end.
          Nothing of the current step is saved. */}
      <SignupLogo />
      <Typography
        id={titleId}
        component="p"
        sx={{
          // The step list is hidden on a phone, and the content says "Step N
          // of M" there; the overline would only lengthen the header band.
          display: { xs: "none", md: "block" },
          mt: { md: fluid(56) },
          mb: { md: "24px" },
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.45,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "rgba(255, 255, 255, 0.7)",
        }}
      >
        {heading}
      </Typography>
      {steps && (
        <List
          component="ol"
          disablePadding
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
          }}
        >
          {steps.map((step, index) => {
            const goal = step.id === "done";
            const linked =
              onStepSelect !== undefined &&
              !goal &&
              (step.status === "completed" ||
                (step.status === "upcoming" && multiStep));
            const content = (
              <>
                <StepMarker
                  status={step.status}
                  goal={goal}
                  number={index + 1}
                  completedLabel={t("Completed", { ns: "Global" })}
                />
                <Typography
                  component="span"
                  className="onboarding-step-title"
                  sx={{
                    fontSize: 15,
                    lineHeight: 1.45,
                    letterSpacing: "normal",
                    fontWeight: goal
                      ? step.status === "upcoming"
                        ? 400
                        : 700
                      : step.status === "current"
                        ? 700
                        : 500,
                    textAlign: "left",
                    color:
                      step.status !== "upcoming"
                        ? "common.white"
                        : goal
                          ? "rgba(255, 255, 255, 0.7)"
                          : "rgba(255, 255, 255, 0.8)",
                  }}
                >
                  {stepLabels[step.id]}
                </Typography>
              </>
            );
            return (
              <ListItem
                key={step.id}
                disablePadding
                aria-current={step.status === "current" ? "step" : undefined}
                sx={{
                  alignItems: "center",
                  py: "14px",
                  ...(goal && {
                    mt: "6px",
                    pt: "20px",
                    borderTop: "1px solid rgba(255, 255, 255, 0.14)",
                  }),
                }}
              >
                {linked ? (
                  <ButtonBase
                    onClick={() => onStepSelect(step.id)}
                    sx={{
                      gap: "16px",
                      borderRadius: "4px",
                      "&:hover .onboarding-step-title": {
                        textDecoration: "underline",
                      },
                      "&:hover .onboarding-step-marker, &:focus-visible .onboarding-step-marker":
                        { bgcolor: "rgba(255, 255, 255, 0.12)" },
                      "&.Mui-focusVisible": {
                        outline: "2px solid #fff",
                        outlineOffset: "4px",
                      },
                    }}
                  >
                    {content}
                  </ButtonBase>
                ) : (
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "16px" }}
                  >
                    {content}
                  </Box>
                )}
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};

const StepMarker = ({
  status,
  goal,
  number,
  completedLabel,
}: {
  status: OnboardingStepStatus;
  /** The last step ("Ready to go"): a bare check until it is reached. */
  goal: boolean;
  number: number;
  completedLabel: string;
}) => (
  <Box
    className="onboarding-step-marker"
    sx={{
      flex: "0 0 auto",
      width: 28,
      height: 28,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.45,
      letterSpacing: "normal",
      ...(status === "current" || (goal && status === "completed")
        ? { bgcolor: "common.white", color: ONBOARDING_COLORS.brandPanel }
        : goal
          ? { color: "rgba(255, 255, 255, 0.7)" }
          : {
              border: "1.5px solid",
              borderColor:
                status === "completed"
                  ? "common.white"
                  : "rgba(255, 255, 255, 0.45)",
              color:
                status === "completed"
                  ? "common.white"
                  : "rgba(255, 255, 255, 0.8)",
            }),
    }}
  >
    {status === "completed" || goal ? (
      <CheckIcon
        sx={{
          fontSize: goal && status !== "completed" ? 20 : 16,
          color: goal && status === "completed" ? "inherit" : "common.white",
        }}
        titleAccess={status === "completed" ? completedLabel : undefined}
        aria-hidden={status === "completed" ? undefined : true}
      />
    ) : (
      number
    )}
  </Box>
);
